import { LayerService } from '@/core/services/open-api/layer.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Layer } from '@/core/models/layer';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { CurrentUser } from '@/core/models/user';
import { UserService } from '@/core/services/open-api/user.service';
import { ToastService } from '@/core/services/toast.service';

@Component({
    selector: 'app-layer',
    templateUrl: './layer.component.html',
    styleUrls: ['./layer.component.scss'],
    standalone: false,
})
export class LayerComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    isPro = false;
    isLoading = true;
    changedName? = '';
    layers: Layer[] = [];
    stateOptions = [
        { label: 'Off', value: false },
        { label: 'On', value: true },
    ];
    private subscriptions = new Subscription();

    constructor(
        private readonly layerService: LayerService,
        public readonly userService: UserService,
        private readonly toastService: ToastService,
    ) {}

    ngOnInit(): void {
        this.subscriptions.add(
            this.layerService.layers$.subscribe((layers: Layer[]) => {
                this.layers = layers;
                this.isLoading = false;
            }),
        );

        this.subscriptions.add(
            this.userService.currentUser$.subscribe((currentUser: CurrentUser) => {
                this.isPro = currentUser.isPro();
            }),
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggleLayerVisibility(layer: Layer, value: boolean): void {
        if (value === layer.visible) {
            return;
        }

        layer.visible = value;

        this.updateLayer(layer);
    }

    private updateLayer(layer: Layer): void {
        this.isLoading = true;

        this.layerService
            .update(layer)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.isLoading = false;
                },
                error: (error) => {
                    console.debug(error);
                    const index = layer.index ?? 0;
                    this.layers[index - 1].visible = !layer.visible;
                    this.layers = [...this.layers];
                    this.isLoading = false;
                },
            });
    }

    onRowEditInit(layer: Layer): void {
        this.changedName = layer.name;
    }

    private isNameAlreadyUsedByAnotherLayer(layerName: string, layerIndex: number): boolean {
        for (const layer of this.layers) {
            if (layer.index !== layerIndex) {
                if (layerName === layer.name) {
                    return true;
                }
            }
        }
        return false;
    }

    onRowEditSave(layer: Layer): void {
        if (this.changedName === undefined) {
            // this case must not happen!
            this.toastService.toast('UNEXPECTED_ERROR');
            return;
        }
        const changedName = this.changedName.trim();
        // if user tries to save empty name, reset the name to previous value
        if (changedName === '') {
            this.changedName = layer.name;
            this.toastService.error('standard_error', 'settings_general_layer_empty_name_error');
            return;
        }
        if (this.isNameAlreadyUsedByAnotherLayer(changedName, layer.index!)) {
            this.changedName = layer.name;
            this.toastService.error('standard_error', 'settings_general_layer_duplicate_name_error');
            return;
        }
        layer.name = changedName;
        this.updateLayer(layer);
    }

    onRowEditCancel(_layer: Layer, _index: number): void {}

    updateName(name: string): void {
        this.changedName = name;
    }

    isSelectButtonDisabled(layer: Layer): boolean {
        const parent = this.layers.findIndex((l) => l.id === layer.parent);
        const child = this.layers.findIndex((l) => l.id === layer.child);

        if (this.layers[child]?.visible || !this.isPro) {
            return true;
        } else if (this.layers[parent]?.visible) {
            return false;
        }

        return true;
    }
}
